import { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, Plus, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/ai-management`;

const CONTROL_TYPES = [
  { value: 'disable_all_video', label: 'Disable All Video Generation', icon: '🎥' },
  { value: 'disable_all_image', label: 'Disable All Image Generation', icon: '🖼️' },
  { value: 'disable_all_audio', label: 'Disable All Audio Generation', icon: '🎵' },
  { value: 'pause_provider', label: 'Pause Specific Provider', icon: '⏸️' },
  { value: 'emergency_cost_cap', label: 'Emergency Cost Cap', icon: '💰' },
];

export default function SystemControls() {
  const [controls, setControls] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    control_type: 'disable_all_video',
    enabled: true,
    reason: '',
    target_id: '',
    threshold_value: '',
  });

  useEffect(() => {
    fetchProviders();
    fetchControls();
  }, []);

  const fetchProviders = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/providers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProviders(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const fetchControls = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/system-controls`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setControls(response.data);
    } catch (error) {
      console.error('Error fetching controls:', error);
      toast.error('Failed to fetch system controls');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...formData,
        target_id: formData.target_id || null,
        threshold_value: formData.threshold_value ? parseFloat(formData.threshold_value) : null,
      };
      
      await axios.post(`${API}/system-controls`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('System control created');
      setShowDialog(false);
      resetForm();
      fetchControls();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create control');
    }
  };

  const resetForm = () => {
    setFormData({
      control_type: 'disable_all_video',
      enabled: true,
      reason: '',
      target_id: '',
      threshold_value: '',
    });
  };

  const getControlIcon = (type) => {
    const control = CONTROL_TYPES.find(c => c.value === type);
    return control ? control.icon : '⚙️';
  };

  if (loading) return <div className="flex items-center justify-center h-full"><div className="text-lg text-slate-600">Loading...</div></div>;

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">System Controls</h1>
          <p className="text-slate-600">Emergency controls and kill switches</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} className="bg-red-600 hover:bg-red-700">
          <Plus className="w-4 h-4 mr-2" /> Add Control
        </Button>
      </div>

      {/* Active Controls Warning */}
      {controls.filter(c => c.enabled).length > 0 && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 mb-1">Active System Controls</h3>
              <p className="text-sm text-red-800">
                {controls.filter(c => c.enabled).length} control(s) currently active - some features may be disabled
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Controls List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {controls.map((control) => (
          <div key={control.control_id} className={`rounded-lg shadow-md p-6 ${control.enabled ? 'bg-red-50 border-2 border-red-200' : 'bg-white'}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center">
                <span className="text-3xl mr-3">{getControlIcon(control.control_type)}</span>
                <div>
                  <h3 className="font-semibold text-slate-800">{CONTROL_TYPES.find(c => c.value === control.control_type)?.label}</h3>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${control.enabled ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>
                    {control.enabled ? 'ACTIVE' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <div className="text-xs text-slate-500 mb-1">Reason:</div>
              <p className="text-sm text-slate-700">{control.reason}</p>
            </div>

            {control.threshold_value && (
              <div className="text-xs text-slate-600 mb-2">Threshold: {control.threshold_value}</div>
            )}

            <div className="text-xs text-slate-500">Created: {new Date(control.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create System Control</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Control Type *</Label>
                <Select value={formData.control_type} onValueChange={(value) => setFormData({...formData, control_type: value})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTROL_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.icon} {type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {formData.control_type === 'pause_provider' && (
                <div>
                  <Label>Target Provider</Label>
                  <Select value={formData.target_id} onValueChange={(value) => setFormData({...formData, target_id: value})}>
                    <SelectTrigger><SelectValue placeholder="Select provider" /></SelectTrigger>
                    <SelectContent>
                      {providers.map(p => (
                        <SelectItem key={p.provider_id} value={p.provider_id}>{p.provider_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {formData.control_type === 'emergency_cost_cap' && (
                <div>
                  <Label>Cost Threshold ($)</Label>
                  <input className="w-full px-3 py-2 border rounded" type="number" step="0.01" placeholder="1000" value={formData.threshold_value} onChange={(e) => setFormData({...formData, threshold_value: e.target.value})} />
                </div>
              )}

              <div>
                <Label>Reason *</Label>
                <Textarea placeholder="Why is this control being applied?" value={formData.reason} onChange={(e) => setFormData({...formData, reason: e.target.value})} required rows={3} />
              </div>

              <div className="flex items-center space-x-2">
                <input type="checkbox" id="enabled" checked={formData.enabled} onChange={(e) => setFormData({...formData, enabled: e.target.checked})} />
                <Label htmlFor="enabled" className="cursor-pointer">Enable immediately</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700">Create Control</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
